/**
 * Notification Navigation Helper
 * Provides utilities for navigating based on notification type and reference ID
 */

export const NOTIFICATION_TYPES = {
  TASK_REMINDER: "task_reminder",
  TASK_DUE_TODAY: "task_due_today",
  TASK_OVERDUE: "task_overdue",
  PROJECT_UPDATE: "project_update",
  APPROVAL_REQUEST: "approval_request",
  APPROVAL_APPROVED: "approval_approved",
  APPROVAL_REJECTED: "approval_rejected",
  DOCUMENT_UPDATE: "document_update",
  ESCALATION: "escalation",
  WORKFLOW_TRANSITION: "workflow_transition",
  SYSTEM_ALERT: "system_alert",
  ANNOUNCEMENT: "announcement",
  BULK_NOTIFICATION: "bulk_notification",
  CUSTOM: "custom",
};

/**
 * Get navigation URL based on notification type and reference ID
 * @param {string} notificationType - Type of notification
 * @param {string} referenceId - ID of the referenced item (task, project, etc.)
 * @param {string} urlPrefix - URL prefix (e.g., "demo")
 * @returns {string} - Navigation URL
 */
export const getNotificationNavigationUrl = (
  notificationType,
  referenceId,
  urlPrefix = "demo"
) => {
  try {
    if (!notificationType) {
      return `/${urlPrefix}/dashboard`;
    }

    const baseUrl = `/${urlPrefix}`;

    switch (notificationType) {
      // Task-related notifications
      case NOTIFICATION_TYPES.TASK_REMINDER:
      case NOTIFICATION_TYPES.TASK_DUE_TODAY:
      case NOTIFICATION_TYPES.TASK_OVERDUE:
        return referenceId
          ? `${baseUrl}/tasks/view/${referenceId}`
          : `${baseUrl}/tasks`;

      // Project-related notifications
      case NOTIFICATION_TYPES.PROJECT_UPDATE:
        return referenceId
          ? `${baseUrl}/projects/view/${referenceId}`
          : `${baseUrl}/projects`;

      // Approval notifications
      case NOTIFICATION_TYPES.APPROVAL_REQUEST:
      case NOTIFICATION_TYPES.APPROVAL_APPROVED:
      case NOTIFICATION_TYPES.APPROVAL_REJECTED:
        return referenceId
          ? `${baseUrl}/approvals/view/${referenceId}`
          : `${baseUrl}/approvals`;

      // Document notifications
      case NOTIFICATION_TYPES.DOCUMENT_UPDATE:
        return referenceId
          ? `${baseUrl}/documents/view/${referenceId}`
          : `${baseUrl}/documents`;

      // Escalation/workflow notifications
      case NOTIFICATION_TYPES.ESCALATION:
      case NOTIFICATION_TYPES.WORKFLOW_TRANSITION:
        return referenceId
          ? `${baseUrl}/tasks/view/${referenceId}`
          : `${baseUrl}/dashboard`;

      // System/announcement notifications
      case NOTIFICATION_TYPES.SYSTEM_ALERT:
      case NOTIFICATION_TYPES.ANNOUNCEMENT:
      case NOTIFICATION_TYPES.BULK_NOTIFICATION:
        return `${baseUrl}/dashboard`;

      // Custom notifications - try to extract action URL
      case NOTIFICATION_TYPES.CUSTOM:
      default:
        return `${baseUrl}/dashboard`;
    }
  } catch (error) {
    console.error("[NotificationNavigationHelper] Error getting URL:", error);
    return `/${urlPrefix}/dashboard`;
  }
};

/**
 * Handle notification click navigation
 * @param {object} notification - Notification object with properties
 * @param {function} navigationCallback - Callback function to execute navigation
 * @param {string} urlPrefix - URL prefix for routes
 */
export const handleNotificationNavigation = (
  notification,
  navigationCallback,
  urlPrefix = "demo"
) => {
  try {
    if (!notification || !navigationCallback) {
      console.warn("[NotificationNavigationHelper] Missing notification or callback");
      return;
    }

    // Priority 1: Use custom action URL if provided
    if (notification.actionUrl || notification.action_url) {
      const customUrl = notification.actionUrl || notification.action_url;
      navigationCallback(customUrl);
      return;
    }

    // Priority 2: Use reference ID with notification type
    const referenceId = notification.referenceId || notification.reference_id;
    const notificationType =
      notification.notificationType || notification.notification_type;

    const url = getNotificationNavigationUrl(notificationType, referenceId, urlPrefix);
    navigationCallback(url);

    // Log for debugging
    console.log("[NotificationNavigationHelper] Navigation triggered:", {
      type: notificationType,
      referenceId: referenceId,
      navigationUrl: url,
    });
  } catch (error) {
    console.error("[NotificationNavigationHelper] Error handling navigation:", error);
    // Fallback to dashboard
    navigationCallback(`/${urlPrefix}/dashboard`);
  }
};

/**
 * Get user-friendly description of notification type
 * @param {string} notificationType - Type of notification
 * @returns {string} - Human-readable notification type
 */
export const getNotificationTypeLabel = (notificationType) => {
  const labels = {
    [NOTIFICATION_TYPES.TASK_REMINDER]: "Task Reminder",
    [NOTIFICATION_TYPES.TASK_DUE_TODAY]: "Task Due Today",
    [NOTIFICATION_TYPES.TASK_OVERDUE]: "Task Overdue",
    [NOTIFICATION_TYPES.PROJECT_UPDATE]: "Project Update",
    [NOTIFICATION_TYPES.APPROVAL_REQUEST]: "Approval Request",
    [NOTIFICATION_TYPES.APPROVAL_APPROVED]: "Approval Approved",
    [NOTIFICATION_TYPES.APPROVAL_REJECTED]: "Approval Rejected",
    [NOTIFICATION_TYPES.DOCUMENT_UPDATE]: "Document Update",
    [NOTIFICATION_TYPES.ESCALATION]: "Escalation",
    [NOTIFICATION_TYPES.WORKFLOW_TRANSITION]: "Workflow Update",
    [NOTIFICATION_TYPES.SYSTEM_ALERT]: "System Alert",
    [NOTIFICATION_TYPES.ANNOUNCEMENT]: "Announcement",
    [NOTIFICATION_TYPES.BULK_NOTIFICATION]: "Bulk Notification",
    [NOTIFICATION_TYPES.CUSTOM]: "Custom Notification",
  };

  return labels[notificationType] || "Notification";
};

/**
 * Get notification icon/color based on type
 * @param {string} notificationType - Type of notification
 * @returns {object} - Icon and color properties
 */
export const getNotificationTypeStyle = (notificationType) => {
  const styles = {
    [NOTIFICATION_TYPES.TASK_REMINDER]: { color: "blue", icon: "⏰" },
    [NOTIFICATION_TYPES.TASK_DUE_TODAY]: { color: "orange", icon: "📅" },
    [NOTIFICATION_TYPES.TASK_OVERDUE]: { color: "red", icon: "⚠️" },
    [NOTIFICATION_TYPES.PROJECT_UPDATE]: { color: "green", icon: "📊" },
    [NOTIFICATION_TYPES.APPROVAL_REQUEST]: { color: "purple", icon: "✅" },
    [NOTIFICATION_TYPES.APPROVAL_APPROVED]: { color: "green", icon: "✓" },
    [NOTIFICATION_TYPES.APPROVAL_REJECTED]: { color: "red", icon: "✗" },
    [NOTIFICATION_TYPES.DOCUMENT_UPDATE]: { color: "gray", icon: "📄" },
    [NOTIFICATION_TYPES.ESCALATION]: { color: "red", icon: "🔥" },
    [NOTIFICATION_TYPES.WORKFLOW_TRANSITION]: { color: "blue", icon: "→" },
    [NOTIFICATION_TYPES.SYSTEM_ALERT]: { color: "orange", icon: "🔔" },
    [NOTIFICATION_TYPES.ANNOUNCEMENT]: { color: "blue", icon: "📢" },
    [NOTIFICATION_TYPES.BULK_NOTIFICATION]: { color: "gray", icon: "📨" },
  };

  return styles[notificationType] || { color: "gray", icon: "ℹ️" };
};

export default {
  NOTIFICATION_TYPES,
  getNotificationNavigationUrl,
  handleNotificationNavigation,
  getNotificationTypeLabel,
  getNotificationTypeStyle,
};
